import { z } from 'zod';
import { ACTION_STATUSES } from './actions';
import {
  AGENDA_ITEM_KINDS,
  DECISION_APPROVAL_STATUSES,
  agendaSnapshotSchema,
  decisionSchema,
} from './critical-analysis';
import { decisionOutcomeSchema } from './decision-outcome';
import { MEETING_LEVELS, MEETING_STATUSES, MEETING_TYPES } from './meetings';

// ---------------------------------------------------------------------------------------------
// Ata da reunião e histórico de decisões (PRD §11.25 "depois da reunião", §19, Anexo C; DEC-051).

const ref = z.object({ id: z.string().uuid(), name: z.string() });
const areaIds = {
  /** Área/unidade (para mostrar só o que está no escopo de quem pede — DEC-016). */
  departmentId: z.string().uuid().nullable(),
  businessUnitId: z.string().uuid().nullable(),
};

export const minutesDecisionSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  responsibleName: z.string().nullable(),
  dueDate: z.string().nullable(),
  done: z.boolean(),
  /** A ação criada, como estava no encerramento. */
  action: z
    .object({
      id: z.string().uuid(),
      title: z.string(),
      status: z.enum(ACTION_STATUSES),
      planId: z.string().uuid(),
      planName: z.string(),
    })
    .nullable(),
  createdByName: z.string(),
  /** Estado da aprovação no encerramento (DEC-053); atas anteriores à aprovação: sem aprovação. */
  approvalStatus: z.enum(DECISION_APPROVAL_STATUSES).default('NONE'),
  ...areaIds,
});
export type MinutesDecision = z.infer<typeof minutesDecisionSchema>;

export const minutesItemSchema = z.object({
  id: z.string().uuid(),
  kind: z.enum(AGENDA_ITEM_KINDS),
  title: z.string(),
  detail: z.string().nullable(),
  department: ref.nullable(),
  businessUnit: ref.nullable(),
  ...areaIds,
  /** Retrato quando a pauta foi montada e no encerramento. */
  snapshot: agendaSnapshotSchema.nullable(),
  closing: agendaSnapshotSchema.nullable(),
  justification: z.string().nullable(),
  discussed: z.boolean(),
  discussedByName: z.string().nullable(),
  decisions: z.array(minutesDecisionSchema),
});
export type MinutesItem = z.infer<typeof minutesItemSchema>;

/** A ata gravada no encerramento (o registro do que valia naquele momento). */
export const minutesDocumentSchema = z.object({
  generatedAt: z.string(),
  meeting: z.object({
    id: z.string().uuid(),
    type: z.enum(MEETING_TYPES),
    level: z.enum(MEETING_LEVELS),
    title: z.string(),
    startsAt: z.string(),
    durationMinutes: z.number().int(),
    startedAt: z.string().nullable(),
    closedAt: z.string(),
    department: ref.nullable(),
    businessUnit: ref.nullable(),
    createdByName: z.string(),
    closedByName: z.string(),
  }),
  participants: z.array(ref),
  /** Pauta livre e registro do que foi tratado. */
  agenda: z.string().nullable(),
  record: z.string().nullable(),
  items: z.array(minutesItemSchema),
  /** Próxima reunião agendada no encerramento. */
  next: z.object({ id: z.string().uuid(), title: z.string(), startsAt: z.string() }).nullable(),
});
export type MinutesDocument = z.infer<typeof minutesDocumentSchema>;

/** A ata como quem pede a vê: só os itens e decisões no escopo dela, com a contagem do que ficou de fora. */
export const minutesSchema = minutesDocumentSchema.extend({
  hiddenItems: z.number().int(),
  hiddenDecisions: z.number().int(),
});
export type Minutes = z.infer<typeof minutesSchema>;

export const decisionHistorySchema = z.object({
  items: z.array(
    z.object({
      decision: decisionSchema,
      meeting: z.object({
        id: z.string().uuid(),
        title: z.string(),
        startsAt: z.string(),
        level: z.enum(MEETING_LEVELS),
        status: z.enum(MEETING_STATUSES),
      }),
      /** Por que estava crítico e o que foi discutido. */
      item: z.object({
        id: z.string().uuid(),
        kind: z.enum(AGENDA_ITEM_KINDS),
        title: z.string(),
        detail: z.string().nullable(),
        snapshot: agendaSnapshotSchema.nullable(),
        justification: z.string().nullable(),
        discussed: z.boolean(),
        kpi: ref.nullable(),
        objective: ref.nullable(),
      }),
      /** O indicador (ou o objetivo) melhorou depois do prazo. */
      outcome: decisionOutcomeSchema,
    }),
  ),
});
export type DecisionHistory = z.infer<typeof decisionHistorySchema>;
